import { Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ClockIcon } from '@tix-ticketing/ui-icons/outline';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ExtendTimerDialog } from 'components/common/dialogs/extend-timer-dialog';
import { FormattedDuration } from 'components/common/formatted-duration/formatted-duration';
import { useAppState } from 'lib/app-state';
import { secondsUntil } from 'lib/helpers/date-helpers';
import { isActiveReservation } from 'lib/helpers/order-helpers';
import { useLocalStorage } from 'lib/hooks/use-storage';
export function OrderTimer() {
  const appState = useAppState();
  const {
    eventData,
    setEventData
  } = appState;
  const {
    order
  } = eventData;
  const [showExtendTimerDialog, setShowExtendTimerDialog] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const [visible, setVisible] = useState(false);

  // The order's time can only be extended once. We persist the id of the order
  // that has already been extended so the limit survives reloads.
  const [extendedOrderId, setExtendedOrderId] = useLocalStorage<string | null>({
    key: '@extendedOrderId',
    defaultValue: null
  });
  const hasExtended = !!order?.encryptedId && extendedOrderId === order.encryptedId;

  // Only a live, unpaid reservation should drive the countdown.
  const activeReservation = isActiveReservation(order);
  useEffect(() => {
    if (!order?.expires || !activeReservation) {
      setVisible(false);
      return;
    }
    const checkVisibility = () => {
      setVisible(secondsUntil(order.expires) <= 5 * 60);
    };
    checkVisibility();
    const interval = setInterval(checkVisibility, 1000);
    return () => clearInterval(interval);
  }, [order?.expires, activeReservation]);
  if (!visible || !activeReservation) return null;
  return <>
      {order && order.expires && <Tooltip title={<FormattedMessage id="cart.timeLeft" defaultMessage={[{
      "type": 0,
      "value": "Time left to complete your order"
    }]} />} placement="top">
          <Button variant="contained" color="monochrome" startIcon={<ClockIcon />} size={matches ? 'medium' : 'small'}
      // Once the time has been extended the dialog can no longer be
      // reopened, so the timer is only clickable before that.
      onClick={hasExtended ? undefined : () => setShowExtendTimerDialog(true)} sx={{
        mr: 1,
        '&:focus': {
          outline: 'none',
          boxShadow: 'none'
        },
        '&:hover': {
          backgroundColor: 'monochrome.main'
        },
        cursor: hasExtended ? 'default' : 'pointer'
      }}>
            <FormattedDuration value={secondsUntil(order.expires)} style="short" updateIntervalInSeconds={1} onExpiryWarning={() => {
          if (!hasExtended) setShowExtendTimerDialog(true);
        }} onExpire={() => {
          setEventData({
            event: undefined,
            order: undefined
          });
        }} />
          </Button>
        </Tooltip>}

      <ExtendTimerDialog encryptedOrderId={order?.encryptedId ?? ''} open={showExtendTimerDialog} onClose={() => setShowExtendTimerDialog(false)} onExtended={() => {
      if (order?.encryptedId) setExtendedOrderId(order.encryptedId);
    }} />
    </>;
}